import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
    filtersSelector,
    navSelector
} from 'common/store/modules/competition-nav/selectors';
import { useGetContentQuery } from 'common/store/modules/api/content-api';
import { ConditionalWrapper } from 'common/react/components/ConditionalWrapper';
import { LazyImage } from 'common/react/components/LazyImage';

const TWO_X = 2;
const THREE_X = 3;
const mediaQueryConfig = [
    {
        name: 'small-mobile',
        maxWidth: 374,
        size: { width: 375, height: 80 },
        multipliers: [1, TWO_X, THREE_X]
    },
    {
        name: 'mobile',
        maxWidth: 767,
        size: { width: 768, height: 80 },
        multipliers: [1, TWO_X, THREE_X]
    },
    {
        name: 'desktop',
        minWidth: 768,
        size: { height: 80, width: 1424 },
        multipliers: [1, TWO_X]
    },
    {
        name: 'default',
        size: { height: 80, width: 1424 },
        multipliers: [1]
    }
];

export const MarketingBanner = () => {
    const nav = useSelector(navSelector);
    const competition = nav?.competition.id;
    const season = nav?.season.id;
    const round = nav?.round.id;

    const filters = useSelector(filtersSelector);

    const isRoundView =
        filters?.cities.length > 0 ||
        filters?.teams.length > 0 ||
        filters?.venues.length > 0;

    // Expression for all banners with either correct comp, season or round.
    const refExpression = `(AFL_COMPETITION:${competition}) or (AFL_COMPSEASON:${season}) or (AFL_ROUND:${round})`;

    const { data: promos } = useGetContentQuery(
        {
            type: 'PROMO',
            params: {
                limit: 50,
                tagNames: 'marketing-promo-banner',
                referenceExpression: refExpression
            }
        },
        {
            skip: !competition || !season || !round
        }
    );

    // Once we have all banners, we can then filter them to only show the ones we need.
    const bannersToShow = useMemo(() => {
        return promos?.content?.filter((promo) => {
            let shown = true;

            // If a banner has a compseason reference
            if (
                promo.references?.find((ref) => ref.type === 'AFL_COMPSEASON')
            ) {
                const matchedSeason = promo.references.filter(
                    (ref) => ref.type === 'AFL_COMPSEASON' && ref.id === season
                );

                if (!matchedSeason.length) {
                    shown = false;
                }
            }

            // If a banner has a round reference and not in round view
            if (promo.references?.find((ref) => ref.type === 'AFL_ROUND')) {
                const matchedRound = promo.references.filter(
                    (ref) => ref.type === 'AFL_ROUND' && ref.id === round
                );

                if (!matchedRound.length || isRoundView) {
                    shown = false;
                }
            }

            return shown;
        });
    }, [promos?.content, season, round, isRoundView]);

    const handleClick = (banner) => {
        const url = new URL(banner.links[0].promoUrl, location.origin);

        PULSE.app.tracking.recordEvent(
            PULSE.app.common.CONSTANTS.TRACKING.EVENTS.SELECT.CTA,
            {
                /* eslint-disable camelcase */
                eventData: {
                    link_text: banner.links[0].linkText,
                    link_url: banner.links[0].promoUrl,
                    outbound: url.origin !== location.origin,
                    type:
                        url.origin !== location.origin
                            ? 'external'
                            : 'internal',
                    component_name: `${banner.title}`,
                    component_content_type:
                        PULSE.app.common.CONSTANTS.TRACKING.CONTENT_TYPES
                            .MARKETING_PROMO
                }
                /* eslint-enable camelcase */
            }
        );
    };

    return !bannersToShow?.length
        ? null
        : bannersToShow.map((banner) => {
              const image =
                  PULSE.app.common.content.getImageFromContentItem(banner);

              return (
                  <div
                      className="wrapper marketing-promo-banner"
                      key={banner.id}
                  >
                      <div className="marketing-promo-banner__image">
                          <ConditionalWrapper
                              condition={banner.links[0]}
                              wrapper={(children) => (
                                  <a
                                      href={banner.links[0].promoUrl ?? ''}
                                      aria-label={
                                          banner.links[0].linkText ??
                                          banner.title
                                      }
                                      target={
                                          banner.links[0].external
                                              ? '_blank'
                                              : ''
                                      }
                                      rel="noreferrer"
                                      onClick={() => handleClick(banner)}
                                  >
                                      {children}
                                  </a>
                              )}
                          >
                              <LazyImage
                                  image={image}
                                  mediaQueryConfig={mediaQueryConfig}
                                  cssClass="js-faded-image fade-in-on-load object-fit-cover-picture__img is-loaded"
                              />
                          </ConditionalWrapper>
                      </div>
                  </div>
              );
          });
};
